import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Seo } from "../components/Seo";
import { GovernedLifecycle } from "../components/GovernedLifecycle";
import { Button } from "../components/ui/button";
import { 
  Shield, 
  Users, 
  Settings, 
  Eye,
  ChevronRight,
  CheckCircle,
  FileText,
  ExternalLink
} from "lucide-react";

// MGF Dimensions mapped to SwarmCitadel capabilities
const mgfDimensions = [
  {
    number: "1",
    title: "Assessing and Bounding Risk",
    frameworkAsks: "Identify and quantify risks of agent actions before and during execution. Bound agent autonomy. Account for multi-agent interaction and system complexity.",
    architectureProvides: "Pre-execution evaluation of every proposed action against deterministic policy. Risk tiers that map to different handling (execute, require approval, block). Fleet-level visibility for correlated failures across agents.",
    swarmcitadelImplements: [
      "Deterministic policy engine evaluates every action pre-execution (<100ms)",
      "Composable rules with defined conflict-resolution strategies",
      "Fail-closed enforcement for high-risk action classes",
      "Fleet-level crisis detection and policy-defined containment (roadmap)"
    ],
    icon: Shield,
    color: "violet",
    status: "production"
  },
  {
    number: "2",
    title: "Meaningful Human Accountability",
    frameworkAsks: "A human must own agent decisions in a way that is real rather than ceremonial. The accountability chain must be auditable. Monitor for automation bias.",
    architectureProvides: "Approval gates calibrated to risk. Override channels that work mid-flight. Per-action decision trail linking each action to its policy evaluation and approver. Measurement of approval latencies and override rates.",
    swarmcitadelImplements: [
      "Risk-calibrated approval gates and human-override channels",
      "Every action generates an audit record binding action, policy, and approver",
      "Override rates and approval response times queryable in the ledger",
      "Human-equivalent-work measurement for labor attribution (patent filed)"
    ],
    icon: Users,
    color: "cyan",
    status: "production"
  },
  {
    number: "3",
    title: "Technical Controls and Processes",
    frameworkAsks: "Pre-deployment testing, access boundaries, system-level safeguards. Select control types appropriate to each lifecycle stage. Maintain change-management discipline.",
    architectureProvides: "Structural and rule-based controls in the execution path, not prompt-layer measures. Deterministic controls that hold regardless of what the model does. Versioned policies so historical actions can be interpreted against the exact policy that governed them.",
    swarmcitadelImplements: [
      "Layered access control (authenticated identities, role-based access, workspace scoping)",
      "Decision-level authorization, not just network-level",
      "Declarative, versioned, human-readable policies",
      "Policy changes are themselves recorded and attributable events"
    ],
    icon: Settings,
    color: "green",
    status: "production"
  },
  {
    number: "4",
    title: "End-User Responsibility and Transparency",
    frameworkAsks: "Agent behaviour and constraints must be visible to people affected by them and to regulators. Consider skill degradation and business continuity when agents absorb tasks.",
    architectureProvides: "Offline verifiability: an auditor should be able to verify integrity on their own hardware, with no network connection to the vendor. Tamper-evident records anchored to external commitments.",
    swarmcitadelImplements: [
      "Tamper-evident records hash-linked in sequence",
      "Periodic anchoring to external commitment store (configurable backend)",
      "Regulator-grade export bundles verify offline on commodity hardware",
      "Zero-knowledge policy proofs (compiler built, production roadmap)"
    ],
    icon: Eye,
    color: "amber",
    status: "production"
  }
];

const keyProperties = [
  {
    title: "Cross-Agent Telemetry Correlation",
    description: "Telemetry joinable across agents. An architectural property designed in from the start, not bolted onto per-agent logs later."
  },
  {
    title: "Policy-Defined Graduated Containment",
    description: "Options between 'ignore' and 'shut everything down.' Operators see exactly why the system recommended what it did."
  },
  {
    title: "Boundary-Level Enforcement",
    description: "Gateway-style interception in front of agent egress. Third-party agents governable with no agent code changes."
  }
];

export const GovernancePage = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#lifecycle") setTimeout(() => document.getElementById("lifecycle")?.scrollIntoView({ behavior: "smooth" }), 150);
  }, [hash]);
  return (
    <div className="min-h-screen pt-20" data-testid="governance-page">
      <Seo
        title="MGF Implementation Architecture | SwarmCitadel Governance"
        description="How SwarmCitadel implements Singapore's Model AI Governance Framework for Agentic AI. Runtime governance, human accountability, audit trails, and labor attribution."
        path="/governance"
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh pointer-events-none" />
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-swarm-cyan/10 rounded-full blur-[150px]" />
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="section-label">Governance</div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            MGF Implementation Architecture
          </h1>
          <p className="text-slate-400 text-lg mb-6 max-w-2xl">
            How SwarmCitadel implements Singapore&apos;s Model AI Governance Framework for Agentic AI, dimension by dimension.
          </p>
          <p className="text-slate-500 text-sm">
            Last updated: July 2026
          </p>
        </div>
      </section>

      {/* Framework Overview */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="glass-card p-6 md:p-8 border-swarm-border">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-swarm-violet/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-swarm-violet" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">About the MGF for Agentic AI</h2>
                <p className="text-slate-400 leading-relaxed">
                  The Model AI Governance Framework for Agentic AI was published by Singapore&apos;s IMDA and the AI Verify Foundation in January 2026 and substantially updated in May 2026. It is the first national governance framework written specifically for AI agents that plan, reason, and act autonomously.
                </p>
                <a 
                  href="https://www.imda.gov.sg/-/media/imda/files/about/emerging-tech-and-research/artificial-intelligence/mgf-for-agentic-ai.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-swarm-cyan hover:underline text-sm mt-3"
                >
                  Read the official framework
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Figure 1 — live */}
      <section className="py-12 md:py-16" id="lifecycle" data-testid="governance-lifecycle-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <div className="section-label">Figure 1 · Live</div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">The governed action lifecycle</h2>
            <p className="text-slate-400 leading-relaxed">
              Every proposed agent action is evaluated before execution; every outcome becomes verifiable evidence. This is the architecture the whitepaper maps onto the four MGF dimensions — running live. Propose an action yourself to see the policy engine decide.
            </p>
          </div>
          <GovernedLifecycle />
        </div>
      </section>

      {/* Four Dimensions */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            The Four MGF Dimensions
          </h2>
          
          <div className="space-y-8">
            {mgfDimensions.map((dimension) => {
              const Icon = dimension.icon;
              const colorClasses = {
                violet: "border-swarm-violet/30 bg-swarm-violet/5",
                cyan: "border-swarm-cyan/30 bg-swarm-cyan/5",
                green: "border-swarm-green/30 bg-swarm-green/5",
                amber: "border-amber-500/30 bg-amber-500/5"
              };
              const iconColorClasses = {
                violet: "bg-swarm-violet/10 text-swarm-violet",
                cyan: "bg-swarm-cyan/10 text-swarm-cyan",
                green: "bg-swarm-green/10 text-swarm-green",
                amber: "bg-amber-500/10 text-amber-500"
              };
              const bulletColorClasses = {
                violet: "bg-swarm-violet",
                cyan: "bg-swarm-cyan",
                green: "bg-swarm-green",
                amber: "bg-amber-500"
              };
              
              return (
                <div 
                  key={dimension.number}
                  className={`glass-card p-6 md:p-8 ${colorClasses[dimension.color]}`}
                  data-testid={`mgf-dimension-${dimension.number}`}
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${iconColorClasses[dimension.color]}`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-slate-500 text-sm font-mono">Dimension {dimension.number}</span>
                        {dimension.status === "production" && (
                          <span className="px-2 py-0.5 bg-swarm-green/10 text-swarm-green text-xs rounded-full">
                            In Production
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-white">
                        {dimension.title}
                      </h3>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Framework Requirements */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
                        What the framework asks
                      </h4>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {dimension.frameworkAsks}
                      </p>
                      
                      <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3 mt-6">
                        What an architecture must provide
                      </h4>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {dimension.architectureProvides}
                      </p>
                    </div>

                    {/* SwarmCitadel Implementation */}
                    <div className="bg-swarm-darker/50 rounded-lg p-5">
                      <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-swarm-green" />
                        How SwarmCitadel implements it
                      </h4>
                      <ul className="space-y-3">
                        {dimension.swarmcitadelImplements.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${bulletColorClasses[dimension.color]}`} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Properties Section */}
      <section className="py-16 md:py-24 bg-swarm-darker/30">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 text-center">
            Three Properties That Separate Aligned Architectures
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            The May 2026 update moved the MGF from governing individual agents to governing systems of agents. These three properties are the practical test of alignment.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {keyProperties.map((property, index) => (
              <div key={index} className="glass-card p-6 border-swarm-cyan/20 hover:border-swarm-cyan/40 transition-all">
                <div className="w-10 h-10 rounded-lg bg-swarm-cyan/10 flex items-center justify-center mb-4">
                  <span className="text-swarm-cyan font-bold">{index + 1}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">
                  {property.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {property.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Patent Notice */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="glass-card p-6 border-slate-700/50 bg-slate-800/20">
            <p className="text-slate-400 text-sm leading-relaxed">
              <strong className="text-slate-300">Patent Notice:</strong> Five US provisional patent applications were filed in April 2026 covering tamper-evident action packets, zero-knowledge policy proofs, multi-agent crisis detection, human-equivalent-work attribution, and anchor integrity constructions.
            </p>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-xl font-semibold text-white mb-6">Related Resources</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link 
              to="/whitepapers" 
              className="glass-card p-4 border-swarm-violet/20 hover:border-swarm-violet/40 transition-all flex items-center gap-3"
            >
              <FileText className="w-5 h-5 text-swarm-violet" />
              <div>
                <p className="text-white font-medium">MGF Implementation Whitepaper</p>
                <p className="text-slate-500 text-sm">Full technical paper with architecture details</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-500 ml-auto" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Questions for your governance vendor?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            The whitepaper includes specific questions to ask any vendor, including us. Get the complete paper.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/whitepapers">
              <Button className="btn-glow" data-testid="governance-cta-whitepaper">
                Get the Whitepaper
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-outline" data-testid="governance-cta-contact">
                Talk to Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GovernancePage;
